import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { EmailIntegrationsContext } from "@redotech/redo-merchant-support-app/services/email-integrations";
import type { ReceivedAtEmailCondition as ReceivedAtEmailConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";

export const ReceivedAtEmailCondition = memo(function ReceivedAtEmailCondition({
  condition,
  onChange,
}: {
  condition: ReceivedAtEmailConditionType;
  onChange: (updated: ReceivedAtEmailConditionType) => void;
}) {
  const [availableEmails, setAvailableEmails] = useState<string[]>([]);

  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);
  const team = useRequiredContext(TeamContext);

  useEffect(() => {
    if (!emailIntegrationsLoad.value) {
      return;
    }

    const emails = [
      ...(emailIntegrationsLoad.value.customIntegrations || []),
      ...(emailIntegrationsLoad.value.gmailIntegrations || []),
      ...(emailIntegrationsLoad.value.outlookIntegrations || []),
    ].map((integration) => integration.email);

    const defaultEmail = team.settings?.support?.defaultEmail;
    if (defaultEmail && !emails.includes(defaultEmail)) {
      emails.push(defaultEmail);
    }

    setAvailableEmails((oldEmails) => {
      const updatedEmails = [...oldEmails];
      for (const email of emails) {
        if (!updatedEmails.includes(email)) {
          updatedEmails.push(email);
        }
      }
      return updatedEmails;
    });
  }, [emailIntegrationsLoad.value, team]);

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const emailItems: RedoListItem<string>[] = useMemo(() => {
    const emails = availableEmails.length
      ? availableEmails
      : ["No email addresses configured"];
    return emails.map((email) => ({
      id: email,
      type: "text" as const,
      text: email,
      value: email,
    }));
  }, [availableEmails]);

  const selectedItems: RedoListItem<string>[] = useMemo(() => {
    return (condition.emails || []).map((email) => ({
      id: email,
      type: "text" as const,
      text: email,
      value: email,
    }));
  }, [condition.emails]);

  const displayItem: RedoListItem<string> | undefined = useMemo(() => {
    if (selectedItems.length === 0) {
      return undefined;
    }
    if (selectedItems.length === 1) {
      return selectedItems[0];
    }
    return {
      id: "display",
      type: "text" as const,
      text: `${selectedItems.length} emails selected`,
      value: "",
    };
  }, [selectedItems]);

  const isDisabled = availableEmails.length === 0;

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        Email Address
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={isDisabled}
        isDropdownOpen={dropdownOpen}
        onClick={() => {
          if (!isDisabled) {
            setDropdownOpen(!dropdownOpen);
          }
        }}
        placeholder="Select email addresses"
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={emailItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, emails: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
